"use client";
import { structureQuery } from "@/utils/query.utils";
import Link from "next/link";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useDict } from "@/components/providers/LocaleProvider";


type PaginationProps = {
  /** current page (1-based) */
  curPage: number;
  /** total number of pages */
  totalPages: number;
  /** path to link to (default: "") */
  pathname?: string;
  /** query key to set for the page number (default: "page") */
  queryKey?: string;
  /** existing query params (only single-value keys) */
  queryParams?: Record<string, string>;
  /** optional className for the wrapper */
  className?: string;
};

function buildHref(opts: {
  pathname: string;
  queryParams: Record<string, string>;
  queryKey: string;
  page: number;
}) {
  const { pathname, queryParams, queryKey, page } = opts;
  const qs = structureQuery(queryParams, queryKey, String(page));
  return `${pathname}${qs}`;
}

function getPageItems(totalPages: number) {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  // When more than 5: 1, 2, 3, …, total-1, total
  return [1, 2, 3, "ellipsis", totalPages - 1, totalPages] as const;
}

export default function Pagination({
  curPage,
  totalPages,
  pathname = "",
  queryKey = "page",
  queryParams = {},
  className = "",
}: PaginationProps) {
  const t = useDict();
  if (totalPages <= 0) return null;

  const clampedCurrent = Math.max(1, Math.min(curPage || 1, totalPages));
  const prevPage = Math.max(1, clampedCurrent - 1);
  const nextPage = Math.min(totalPages, clampedCurrent + 1);
  const items = getPageItems(totalPages);

  const baseLink = (page: number) =>
    buildHref({
      pathname,
      queryParams,
      queryKey,
      page,
    });

  const btnBase =
    "inline-flex items-center justify-center min-w-9 min-h-9 max-w-9 max-h-9  rounded-full border text-sm transition";
  const numberBase = "bg-white border-gray-200 hover:border-gray-300 text-gray-700";
  const activeNumber = "bg-primary text-white border-primary hover:border-primary";
  const arrowBase = "bg-white border-gray-200 hover:border-gray-300 text-gray-700";

  return (
    <section
      aria-label={t.pagination.label}
      className={`flex items-center justify-center gap-4 mt-6 ${className}`}
    >
      {/* Left Arrow */}
      <Link
        aria-label={t.pagination.previous}
        href={baseLink(prevPage)}
        className={`${btnBase} ${arrowBase}`}
      >
        <MdKeyboardArrowLeft size={20} />
      </Link>

      {/* Page numbers */}
      {items.map((it, idx) => {
        if (it === "ellipsis") {
          return (
            <span
              key={`ellipsis-${idx}`}
              aria-hidden="true"
              className="px-2 text-gray-400 select-none"
            >
              …
            </span>
          );
        }
        const pageNum = it as number;
        const isActive = pageNum === clampedCurrent;

        return (
          <Link
            key={pageNum}
            href={baseLink(pageNum)}
            aria-current={isActive ? "page" : undefined}
            className={`${btnBase} ${isActive ? activeNumber : numberBase}`}
          >
            {pageNum}
          </Link>
        );
      })}

      {/* Right Arrow */}
      <Link
        aria-label={t.pagination.next}
        href={baseLink(nextPage)}
        className={`${btnBase} ${arrowBase}`}
      >
        <MdKeyboardArrowRight  size={20} />
      </Link>
    </section>
  );
}
